import React, { useState } from 'react';
import { CheckCircle2, Eye, Send, UserCheck, XCircle } from 'lucide-react';
import { Button, Input } from '../ui';
import type { EndUserHandoffView } from '../../hooks/useEndUserHandoff';

/**
 * Handoff strip shown on the two technical sub-steps (Context + Validation).
 *
 * Buyer side: name/share the end user (NONE), watch the pending state
 * (PENDING — the buyer keeps full edit access), or see the sign-off
 * (SIGNED_OFF, with a re-share affordance). The invite email is a backend
 * placeholder, so the copy tells the buyer to nudge the person themselves.
 *
 * End-user side: the "over to you" strip with the sign-off action while the
 * window is open; otherwise an honest view-only notice.
 */
export interface EndUserHandoffBannerProps {
  handoff: EndUserHandoffView;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const whoOf = (handoff: EndUserHandoffView): string => {
  const base = handoff.endUser?.name || handoff.endUser?.email || 'the end user';
  return handoff.endUser?.teamName ? `${base} (${handoff.endUser.teamName})` : base;
};

const dayOf = (iso?: string): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/** Compact inline capture for RFQs that were created without an end user. */
const NameEndUserForm: React.FC<{ handoff: EndUserHandoffView }> = ({ handoff }) => {
  const [name, setName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();

  const submit = async () => {
    const cleaned = email.trim().toLowerCase();
    if (!EMAIL_SHAPE.test(cleaned)) {
      setEmailError('That email doesn’t look right.');
      return;
    }
    setEmailError(undefined);
    try {
      await handoff.share({
        email: cleaned,
        name: name.trim() || undefined,
        teamName: teamName.trim() || undefined,
      });
    } catch {
      /* actionError renders below */
    }
  };

  return (
    <div className="rounded-card border border-line bg-surface px-s6 py-[14px]">
      <p className="m-0 text-row-title text-ink">Working with a technical end user?</p>
      <p className="m-0 mt-[2px] text-body-sm text-ink-secondary">
        Name them and share these steps — they&rsquo;ll get write access here to fill in the technical
        details, view-only everywhere else.
      </p>
      <div className="mt-s4 flex flex-wrap items-end gap-s3">
        <div className="w-[160px]">
          <Input label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya" disabled={handoff.isSharing} />
        </div>
        <div className="w-[150px]">
          <Input label="Team (optional)" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="e.g. Engineering" disabled={handoff.isSharing} />
        </div>
        <div className="w-[240px]">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(undefined); }}
            placeholder="priya@yourcompany.com"
            disabled={handoff.isSharing}
            error={emailError}
          />
        </div>
        <Button size="sm" onClick={submit} disabled={!email.trim()} loading={handoff.isSharing} loadingLabel="Sharing…" iconLeft={<Send />}>
          Share with end user
        </Button>
      </div>
      {handoff.actionError && <ErrorLine message={handoff.actionError} />}
    </div>
  );
};

const ErrorLine: React.FC<{ message: string }> = ({ message }) => (
  <p className="m-0 mt-s2 flex items-center gap-s2 text-meta font-medium text-danger-ink">
    <XCircle className="h-[11px] w-[11px] shrink-0" />
    {message}
  </p>
);

export const EndUserHandoffBanner: React.FC<EndUserHandoffBannerProps> = ({ handoff }) => {
  const who = whoOf(handoff);
  const sharedDay = dayOf(handoff.sharedAt);
  const signedDay = dayOf(handoff.signedOffAt);

  // ——— End-user side ———
  if (handoff.canFill) {
    return (
      <div className="flex flex-wrap items-center gap-s4 rounded-card border border-robbie-border bg-ai-chip px-s6 py-[12px]">
        <UserCheck className="h-4 w-4 shrink-0 text-robbie-ink" />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-row-title text-ink">Over to you — these technical steps are yours</p>
          <p className="m-0 mt-[2px] text-body-sm text-ink-secondary">
            Answer the questions and resolve the checks, then sign off to hand the project back.
          </p>
          {handoff.actionError && <ErrorLine message={handoff.actionError} />}
        </div>
        <Button
          size="sm"
          onClick={() => handoff.signOff().catch(() => undefined)}
          loading={handoff.isSigningOff}
          loadingLabel="Signing off…"
          iconLeft={<CheckCircle2 />}
        >
          {'Sign off & hand back'}
        </Button>
      </div>
    );
  }
  if (handoff.isEndUser) {
    return (
      <div className="flex items-center gap-s4 rounded-card border border-line bg-surface px-s6 py-[12px]">
        <Eye className="h-4 w-4 shrink-0 text-ink-muted" />
        <p className="m-0 text-body-sm text-ink-secondary">
          {handoff.status === 'SIGNED_OFF'
            ? `You signed off${signedDay ? ` on ${signedDay}` : ''} — this project is view-only for you now.`
            : 'This project is view-only for you — the owner will share the technical steps when it’s your turn.'}
        </p>
      </div>
    );
  }

  // ——— Buyer / other org members ———
  if (!handoff.endUser) return <NameEndUserForm handoff={handoff} />;

  if (handoff.status === 'PENDING') {
    return (
      <div className="flex items-center gap-s4 rounded-card border border-warn-border bg-warn-bg px-s6 py-[12px]">
        <UserCheck className="h-4 w-4 shrink-0 text-warn-ink" />
        <p className="m-0 text-body-sm text-ink-body">
          <span className="font-semibold text-ink">Pending on {who}</span>
          {sharedDay ? ` — shared on ${sharedDay}.` : '.'} You can still edit everything and take over
          anytime.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-s4 rounded-card border border-line bg-surface px-s6 py-[12px]">
      {handoff.status === 'SIGNED_OFF' ? (
        <>
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success-ink" />
          <div className="min-w-0 flex-1">
            <p className="m-0 text-body-sm text-ink-body">
              <span className="font-semibold text-ink">{who} signed off</span>
              {signedDay ? ` on ${signedDay}` : ''} — the technical details are ready.
            </p>
            {handoff.actionError && <ErrorLine message={handoff.actionError} />}
          </div>
          <Button size="sm" variant="secondary" onClick={() => handoff.share().catch(() => undefined)} disabled={handoff.isSharing}>
            {handoff.isSharing ? 'Sharing…' : 'Share again'}
          </Button>
        </>
      ) : (
        <>
          <Send className="h-4 w-4 shrink-0 text-action" />
          <div className="min-w-0 flex-1">
            <p className="m-0 text-body-sm text-ink-body">
              <span className="font-semibold text-ink">{who}</span> is this project&rsquo;s end user —
              share these steps when you want them to fill in the technical details.
            </p>
            <p className="m-0 mt-[2px] text-meta text-ink-muted">
              Sharing opens their access and sends them an email heads-up.
            </p>
            {handoff.actionError && <ErrorLine message={handoff.actionError} />}
          </div>
          <Button
            size="sm"
            onClick={() => handoff.share().catch(() => undefined)}
            loading={handoff.isSharing}
            loadingLabel="Sharing…"
            iconLeft={<Send />}
          >
            Share with end user
          </Button>
        </>
      )}
    </div>
  );
};

export default EndUserHandoffBanner;
